/**
 * Linear-style property pickers: a compact trigger (icon + value) that opens
 * a small searchable list (ported from the findings redesign prototype). Built on the product's Popover, Input, Kbd and
 * Tooltip. Arrow keys move, Enter picks, numbers 1–9 pick directly, Escape
 * closes. `open`/`onOpenChange` are controllable so page shortcuts (s, p, a)
 * can open them.
 */
import { CheckIcon } from "lucide-react";
import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from "react";

import { cn } from "~/lib/utils";
import { Kbd } from "../../ui/kbd";
import { Popover, PopoverPopup, PopoverTrigger } from "../../ui/popover";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../../ui/tooltip";

export interface PickerOption {
  readonly value: string;
  readonly label: string;
  readonly icon?: ReactNode;
  readonly hint?: string;
  readonly checked?: boolean;
  readonly disabled?: boolean;
}

export function Picker({
  label,
  shortcut,
  trigger,
  options,
  onPick,
  open: openProp,
  onOpenChange,
  placeholder,
  multiple = false,
  disabled = false,
  align = "start",
  triggerClassName,
}: {
  readonly label: string;
  readonly shortcut?: string;
  readonly trigger: ReactNode;
  readonly options: ReadonlyArray<PickerOption>;
  readonly onPick: (value: string) => void;
  readonly open?: boolean;
  readonly onOpenChange?: (open: boolean) => void;
  readonly placeholder?: string;
  /** Keep the list open after a pick (labels). */
  readonly multiple?: boolean;
  readonly disabled?: boolean;
  readonly align?: "start" | "end" | "center";
  readonly triggerClassName?: string | undefined;
}) {
  const [innerOpen, setInnerOpen] = useState(false);
  const open = openProp ?? innerOpen;
  const setOpen = (next: boolean) => {
    if (onOpenChange) onOpenChange(next);
    else setInnerOpen(next);
  };
  const [query, setQuery] = useState("");
  const [activeIndex, setActive] = useState(0);
  const listId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const filtered = useMemo(
    () => options.filter((option) => option.label.toLowerCase().includes(query.trim().toLowerCase())),
    [options, query],
  );
  // Reset on opening only: callers build `options` each render, and a
  // background refresh must not clear what the person is typing.
  const optionsRef = useRef(options);
  optionsRef.current = options;
  useEffect(() => {
    if (!open) return;
    setQuery("");
    setActive(Math.max(0, optionsRef.current.findIndex((option) => option.checked)));
  }, [open]);
  // A shorter list (new options, a narrower query) never leaves the highlight past its end.
  const active = Math.max(0, Math.min(activeIndex, filtered.length - 1));
  const pick = (option: PickerOption | undefined) => {
    if (!option || option.disabled) return;
    onPick(option.value);
    if (!multiple) setOpen(false);
  };
  const triggerButton = (
    <PopoverTrigger
      disabled={disabled}
      aria-label={label}
      className={cn(
        "flex h-7 min-w-0 max-w-full cursor-pointer items-center gap-2 rounded-md px-1.5 text-[13px] text-foreground outline-none transition-colors duration-150 hover:bg-foreground/[0.06] focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-default disabled:hover:bg-transparent data-popup-open:bg-foreground/[0.06]",
        triggerClassName,
      )}
    >
      {trigger}
    </PopoverTrigger>
  );
  return (
    <Popover open={open} onOpenChange={setOpen}>
      {shortcut ? (
        <Tooltip>
          <TooltipTrigger render={triggerButton} />
          <TooltipPopup side="left">
            {label} <Kbd className="ms-1">{shortcut.toUpperCase()}</Kbd>
          </TooltipPopup>
        </Tooltip>
      ) : (
        triggerButton
      )}
      <PopoverPopup align={align} className="w-64" viewportClassName="p-0 [--viewport-inline-padding:0px]" initialFocus={inputRef}>
        <div className="border-b border-border/70 px-2.5 py-2">
          <input
            ref={inputRef}
            value={query}
            role="combobox"
            aria-expanded
            aria-controls={listId}
            aria-activedescendant={filtered[active] ? `${listId}-${active}` : undefined}
            onChange={(event) => {
              setQuery(event.target.value);
              setActive(0);
            }}
            onKeyDown={(event) => {
              if (event.key === "ArrowDown") {
                event.preventDefault();
                setActive((index) => Math.min(filtered.length - 1, index + 1));
              } else if (event.key === "ArrowUp") {
                event.preventDefault();
                setActive((index) => Math.max(0, index - 1));
              } else if (event.key === "Enter") {
                event.preventDefault();
                pick(filtered[active]);
              } else if (/^[1-9]$/.test(event.key) && !query) {
                event.preventDefault();
                pick(filtered[Number(event.key) - 1]);
              }
            }}
            placeholder={placeholder ?? `${label}…`}
            className="w-full bg-transparent text-[13px] text-foreground outline-none placeholder:text-muted-foreground/70"
          />
        </div>
        <ul id={listId} role="listbox" aria-label={label} className="max-h-64 overflow-y-auto p-1">
          {filtered.length === 0 ? <li className="px-2 py-1.5 text-xs text-muted-foreground">No matches</li> : null}
          {filtered.map((option, index) => (
            <li
              key={option.value}
              id={`${listId}-${index}`}
              role="option"
              aria-selected={option.checked ?? false}
              aria-disabled={option.disabled}
              onMouseEnter={() => setActive(index)}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => pick(option)}
              className={cn(
                "flex h-8 cursor-pointer items-center gap-2 rounded-md px-2 text-[13px] text-foreground transition-colors duration-100",
                index === active && "bg-foreground/[0.07]",
                option.disabled && "cursor-default opacity-50",
              )}
            >
              <span className="flex size-4 shrink-0 items-center justify-center text-muted-foreground">{option.icon}</span>
              <span className="min-w-0 flex-1 truncate">{option.label}</span>
              {option.checked ? <CheckIcon aria-hidden className="size-3.5 text-foreground/70" /> : null}
              {option.hint ? <span className="text-[11px] tabular-nums text-muted-foreground">{option.hint}</span> : null}
            </li>
          ))}
        </ul>
      </PopoverPopup>
    </Popover>
  );
}
